import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import 'screens/Public/DynamicArticle/DynamicArticle.css';
import { useDispatch } from 'react-redux';
import {
  useGetConversationThreadQuery,
  useReplyToMessageMutation,
  useMarkAsReadMutation,
  useAddReactionMutation,
  useRemoveReactionMutation,
} from 'store/services/message/messageApi';
import { handleReactionUpdate } from 'store/services/message/messageCacheUtils';
import { useGetCustomEmojisQuery } from 'store/services/emoji/emojiApi';
import { useMessageSocket } from 'hooks/useMessageSocket';
import { useIsElite } from 'hooks/useIsElite';
import { useGetMeQuery } from 'store/services/user/meApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons';
import BubbleColorPickerModal from 'components/BubbleColorPickerModal/BubbleColorPickerModal';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { ChatImageLightbox } from 'components/ChatImageLightbox/ChatImageLightbox';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { MessageReactionBar } from 'components/MessageReactionBar/MessageReactionBar';
import { ReactionPills } from 'components/ReactionPills/ReactionPills';
import { CustomEmojiUploadModal } from 'components/CustomEmojiUploadModal/CustomEmojiUploadModal';
import { EliteUpsellPrompt } from 'components/EliteUpsellPrompt/EliteUpsellPrompt';
import { isSystemMessage, isSystemConversation } from 'utils/messageHelpers';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import type { Message, MessageImage, MessageWithReadReceipt, ReactionUpdatePayload } from 'types/interfaces/message';
import './ConversationThreadPage.css';

const MAX_IMAGES_PER_MESSAGE = 3;
const MAX_IMAGES_PER_CONVERSATION = 20;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const containsHTML = (text: string) => /<[a-z][\s\S]*?>/i.test(text);

// Convert literal bullet characters (•) to proper Markdown list syntax
const convertBulletsToMarkdown = (content: string): string => {
  // Add newlines before standalone bullets that follow text
  let processed = content.replace(/([^\n])\s*•\s+/g, '$1\n- ');
  // Handle bullets at the very start of the string
  processed = processed.replace(/^•\s+/, '- ');
  return processed;
};

export default function ConversationThreadPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [replyMessage, setReplyMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [lightboxImages, setLightboxImages] = useState<MessageImage[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const previousMessageCountRef = useRef<number>(0);

  // Reaction state
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  const [reactionPickerButtonRef, setReactionPickerButtonRef] = useState<React.RefObject<HTMLButtonElement | null> | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEliteUpsell, setShowEliteUpsell] = useState(false);
  const [showBubbleColorModal, setShowBubbleColorModal] = useState(false);
  const [remainingUploads] = useState(10);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useGetConversationThreadQuery(conversationId!);
  const [replyToMessage, { isLoading: isReplying }] = useReplyToMessageMutation();
  const [markAsRead] = useMarkAsReadMutation();
  const [addReaction] = useAddReactionMutation();
  const [removeReaction] = useRemoveReactionMutation();

  const { data: me } = useGetMeQuery();
  const { isElite } = useIsElite();
  const { data: customEmojis } = useGetCustomEmojisQuery();
  // Get socket for emitting mark as read + reaction updates
  const { emitMarkAsRead } = useMessageSocket({
    enabled: true,
    onReactionUpdate: useCallback((payload: ReactionUpdatePayload) => {
      handleReactionUpdate(dispatch, payload);
    }, [dispatch]),
  });

  // Mark as read immediately when conversation loads
  useEffect(() => {
    if (conversationId && data) {
      // Call REST API for persistence
      markAsRead(conversationId);

      // Also emit via WebSocket for real-time read receipts to sender
      const unreadMessageIds = data.messages
        .filter((m: Message) => !m.isRead && m.sender?.id === data.otherUser.id)
        .map((m: Message) => m.id);

      if (unreadMessageIds.length > 0) {
        emitMarkAsRead(conversationId, unreadMessageIds);
      }
    }
  }, [conversationId, data, markAsRead, emitMarkAsRead]);

  // Auto-scroll chat container to bottom on initial load and when new messages arrive
  // Uses scrollTop instead of scrollIntoView to avoid scrolling the entire page
  useEffect(() => {
    if (data?.messages && messagesContainerRef.current) {
      const currentMessageCount = data.messages.length;

      // Scroll on initial load (previousCount === 0) or when new message added
      if (previousMessageCountRef.current === 0 || currentMessageCount > previousMessageCountRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }

      previousMessageCountRef.current = currentMessageCount;
    }
  }, [data?.messages]);

  // Close reaction picker on scroll so it doesn't float away from the button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !reactionPickerMessageId) return;

    const handleScroll = () => setReactionPickerMessageId(null);
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [reactionPickerMessageId]);

  // Build inline styles for chat bubble gradients
  const getBubbleStyle = (isSent: boolean): React.CSSProperties => {
    if (isSent) {
      const c1 = (me?.isElite && me?.bubbleColor1) || '#8B5CF6';
      const c2 = (me?.isElite && me?.bubbleColor2) || '#6D28D9';
      return { background: `linear-gradient(135deg, ${c1}, ${c2})`, color: '#fff', border: 'none' };
    }
    const c1 = data?.otherUser.bubbleColor1 || '#8B5CF6';
    const c2 = data?.otherUser.bubbleColor2 || '#6D28D9';
    return { background: `linear-gradient(135deg, ${c1}, ${c2})`, color: '#fff', border: 'none' };
  };

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = replyMessage.substring(0, start) + emoji + replyMessage.substring(end);

    setReplyMessage(newMessage);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  // Handle reaction toggle (add or remove)
  const handleToggleReaction = useCallback((messageId: string, emoji: string) => {
    if (!conversationId || !me) return;

    const message = data?.messages.find((m) => m.id === messageId);
    const existingReaction = message?.reactions?.find((r) => r.emoji === emoji);
    const userAlreadyReacted = existingReaction?.reacted;

    if (userAlreadyReacted) {
      removeReaction({ messageId, emoji, conversationId, currentUserId: me.id });
    } else {
      addReaction({ messageId, emoji, conversationId, currentUser: { id: me.id, username: me.username } });
    }

    // Close reaction picker after selecting
    setReactionPickerMessageId(null);
  }, [conversationId, me, data, addReaction, removeReaction]);

  // Handle "Add Emoji" button in reaction picker
  const handleAddCustomEmoji = useCallback(() => {
    setReactionPickerMessageId(null);
    if (isElite) {
      setShowUploadModal(true);
    } else {
      setShowEliteUpsell(true);
    }
  }, [isElite]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conversationImageCount = data?.imageCount ?? 0;

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES_PER_MESSAGE - pendingImages.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message`);
      return;
    }

    const conversationRemaining = MAX_IMAGES_PER_CONVERSATION - conversationImageCount - pendingImages.length;
    if (conversationRemaining <= 0) {
      toast.warning(`This conversation has reached the ${MAX_IMAGES_PER_CONVERSATION} image limit`);
      return;
    }

    const filesToAdd: File[] = [];
    const newPreviewUrls: string[] = [];

    for (const file of files) {
      if (filesToAdd.length >= remainingSlots || filesToAdd.length >= conversationRemaining) {
        toast.warning(`Only ${Math.min(remainingSlots, conversationRemaining)} more image(s) can be added`);
        break;
      }

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not supported. Only JPEG, PNG, and WEBP images are allowed.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${file.name}" is too large. Maximum file size is 5MB.`);
        continue;
      }

      filesToAdd.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    }

    if (filesToAdd.length > 0) {
      setPendingImages(prev => [...prev, ...filesToAdd]);
      setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  }, [pendingImages.length, conversationImageCount]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  }, [addFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((f): f is File => f !== null);

    if (imageFiles.length > 0) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleRemoveImage = useCallback((index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [imagePreviewUrls]);

  const clearPendingImages = useCallback(() => {
    imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setPendingImages([]);
    setImagePreviewUrls([]);
  }, [imagePreviewUrls]);

  const openLightbox = useCallback((images: MessageImage[], index: number) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  }, []);

  const handleEliteRedirect = () => {
    navigate('/elite?source=bubble-colors');
  };

  const handleReply = async () => {
    if ((!replyMessage.trim() && pendingImages.length === 0) || !conversationId) return;

    try {
      await replyToMessage({
        conversationId,
        content: replyMessage.trim(),
        ...(pendingImages.length > 0 ? { images: pendingImages } : {}),
      }).unwrap();

      setReplyMessage('');
      clearPendingImages();
      toast.success('Reply sent!');
    } catch (error: any) {
      console.error('Failed to send reply:', error);
      // 429 errors are now handled globally in baseQuery
      // Only handle other errors here
      if (error.status !== 429) {
        toast.error(error.data?.message || 'Failed to send reply');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="conversation-page">
        <div className="conversation-loading">Loading conversation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversation-page">
        <div className="conversation-error">
          {(error as any).status === 404
            ? "This conversation doesn't exist or you don't have access."
            : 'Failed to load conversation. Please try again.'}
        </div>
        <button onClick={() => navigate('/inbox')} className="back-to-inbox-button">
          Back to Inbox
        </button>
      </div>
    );
  }

  if (!data) return null;

  const formatMessageTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isSystemThread = isSystemConversation(data.otherUser.id, data.otherUser.username);

  return (
    <div className="conversation-page">
      <div className="conversation-header">
        <button onClick={() => navigate('/inbox')} className="back-button">
          ← Back to Inbox
        </button>
        <div className="conversation-user-info">
          <AvatarWithFlag
            avatarUrl={data.otherUser.avatarUrl}
            username={data.otherUser.username}
            countryCode={data.otherUser.country}
            size={40}
            isElite={data.otherUser.isElite}
            showAnimatedBorder={data.otherUser.showAnimatedBorder}
            borderColor1={data.otherUser.borderColor1}
            borderColor2={data.otherUser.borderColor2}
          />
          <UsernameLink
            username={data.otherUser.username}
            isElite={data.otherUser.isElite}
            className="conversation-username conversation-username--link"
          />
        </div>
      </div>

      <div className="conversation-messages" ref={messagesContainerRef}>
        {data.messages.map((message) => {
          const isSystem = isSystemMessage(message);
          return (
            <div
              key={message.id}
              className={`message-bubble ${
                isSystem
                  ? 'system-message'
                  : message.sender?.id === data.otherUser.id ? 'received' : 'sent'
              }`}
            >
              <div className="message-sender-info">
                {isSystem ? (
                  <div className="message-avatar-placeholder system-avatar">S</div>
                ) : (
                  <AvatarWithFlag
                    avatarUrl={message.sender?.avatarUrl}
                    username={message.sender?.username}
                    countryCode={message.sender?.country}
                    size={24}
                    isElite={message.sender?.isElite}
                    showAnimatedBorder={message.sender?.showAnimatedBorder}
                    borderColor1={message.sender?.borderColor1}
                    borderColor2={message.sender?.borderColor2}
                  />
                )}
                {message.sender?.username ? (
                  <UsernameLink
                    username={message.sender.username}
                    isElite={message.sender?.isElite}
                    className="message-sender message-sender--link"
                  />
                ) : (
                  <span className="message-sender">System</span>
                )}
                <span className="message-time">{formatMessageTime(message.createdAt)}</span>
              </div>
              <div className="message-content" style={isSystem ? undefined : getBubbleStyle(message.sender?.id !== data.otherUser.id)}>
                {isSystem ? (
                  containsHTML(message.content) ? (
                    <div
                      className="article-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(message.content, {
                          ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'img', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'span', 'div'],
                          ALLOWED_ATTR: ['href', 'src', 'alt', 'target', 'rel', 'class', 'style', 'data-type', 'text'],
                        })
                      }}
                    />
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => {
                          const isExternal = href?.startsWith('http');
                          const isMailto = href?.startsWith('mailto:');

                          if (isMailto) {
                            return <a href={href}>{children}</a>;
                          }

                          if (isExternal) {
                            return (
                              <a href={href} target="_blank" rel="noopener noreferrer">
                                {children}
                              </a>
                            );
                          }

                          return <Link to={href || '#'}>{children}</Link>;
                        },
                      }}
                    >
                      {convertBulletsToMarkdown(message.content)}
                    </ReactMarkdown>
                  )
                ) : (
                  message.content
                )}
                {message.images && message.images.length > 0 && (
                  <div className="message-images">
                    {message.images.map((img, idx) => (
                      <img
                        key={img.id}
                        src={img.url}
                        alt={`Attachment ${idx + 1}`}
                        className="message-image-thumbnail"
                        loading="lazy"
                        onClick={() => openLightbox(message.images!, idx)}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (!target.dataset.retried) {
                            target.dataset.retried = 'true';
                            // Presigned URL may have expired — refetch will get fresh URLs
                          }
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Reaction bar - appears on hover (non-system messages only) */}
              {!isSystem && (
                <MessageReactionBar
                  onQuickReact={(emoji) => handleToggleReaction(message.id, emoji)}
                  onOpenPicker={(btnRef) => {
                    setReactionPickerMessageId(
                      reactionPickerMessageId === message.id ? null : message.id
                    );
                    setReactionPickerButtonRef(btnRef);
                  }}
                />
              )}

              {/* Reaction pills - displayed below message content */}
              {!isSystem && message.reactions && message.reactions.length > 0 && (
                <ReactionPills
                  reactions={message.reactions}
                  onToggleReaction={(emoji) => handleToggleReaction(message.id, emoji)}
                />
              )}

              {/* Reaction emoji picker for this message */}
              {reactionPickerMessageId === message.id && reactionPickerButtonRef && (
                <EmojiPicker
                  onEmojiSelect={(emoji) => handleToggleReaction(message.id, emoji)}
                  onClose={() => setReactionPickerMessageId(null)}
                  buttonRef={reactionPickerButtonRef}
                  showCustomSection
                  searchable
                  usePortal
                  customEmojis={customEmojis}
                  onAddCustomEmoji={handleAddCustomEmoji}
                />
              )}

              {/* Read receipt for sent messages */}
              {!isSystem && message.sender?.id !== data.otherUser.id && (message as MessageWithReadReceipt).readAt && (
                <div className="message-read-receipt">
                  <span className="read-check">✓</span>
                  <span>Read {formatRelativeTime((message as MessageWithReadReceipt).readAt!)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isSystemThread && (
        <div
          className={`conversation-reply-section${isDragging ? ' drop-zone-active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="reply-form-group">
            <label className="reply-label">Reply</label>
            <textarea
              ref={textareaRef}
              className="reply-textarea"
              placeholder="Type your reply here..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              onPaste={handlePaste}
              rows={4}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={replyMessage.length}
              maxLength={2000}
              warningThreshold={1800}
            />
          </div>

          {imagePreviewUrls.length > 0 && (
            <div className="image-preview-strip">
              {imagePreviewUrls.map((url, index) => (
                <div key={url} className="image-preview-item">
                  <img src={url} alt={`Preview ${index + 1}`} className="image-preview-thumbnail" />
                  <button
                    className="image-preview-remove"
                    onClick={() => handleRemoveImage(index)}
                    type="button"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {conversationImageCount >= MAX_IMAGES_PER_CONVERSATION && (
            <div className="image-limit-warning">
              This conversation has reached the {MAX_IMAGES_PER_CONVERSATION} image limit.
            </div>
          )}

          <div className="reply-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            <button
              className="thread-customize-button"
              onClick={() => setShowBubbleColorModal(true)}
              title="Customize bubble colors"
              type="button"
            >
              <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                  <linearGradient id="palette-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#6F51E4" />
                    <stop offset="100%" stopColor="#E02CDC" />
                  </linearGradient>
                </defs>
              </svg>
              <FontAwesomeIcon icon={faPalette} />
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="image-attach-button"
              onClick={() => fileInputRef.current?.click()}
              type="button"
              title="Attach images"
              disabled={
                pendingImages.length >= MAX_IMAGES_PER_MESSAGE ||
                conversationImageCount >= MAX_IMAGES_PER_CONVERSATION
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>

            <MagicButton
              label={isReplying ? 'Sending...' : 'Send Reply'}
              color="purple"
              onPress={handleReply}
              isDisabled={
                (!replyMessage.trim() && pendingImages.length === 0) ||
                replyMessage.length > 2000 ||
                isReplying
              }
              fullWidth
            />
          </div>
        </div>
      )}

      {lightboxImages && (
        <ChatImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxImages(null)}
        />
      )}

      {/* Custom Emoji Upload Modal */}
      <CustomEmojiUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        remainingUploads={remainingUploads}
      />

      {/* Elite Upsell Modal */}
      <EliteUpsellPrompt
        isOpen={showEliteUpsell}
        onClose={() => setShowEliteUpsell(false)}
      />

      <BubbleColorPickerModal
        isOpen={showBubbleColorModal}
        onClose={() => setShowBubbleColorModal(false)}
        currentColor1={me?.bubbleColor1 || '#8B5CF6'}
        currentColor2={me?.bubbleColor2 || '#6D28D9'}
        onSaveOverride={!isElite ? handleEliteRedirect : undefined}
      />
    </div>
  );
}
